import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { CHIP } from "./AdminView";

function buildReviewLink(appId) {
  return `${window.location.origin}${window.location.pathname}?page=applicant-review&app_id=${appId}`;
}

function ReviewLinkButton({ appId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    const link = buildReviewLink(appId);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(()=>setCopied(false), 2200);
    } catch {
      window.prompt("Copy this review link:", link);
    }
  };
  return (
    <button onClick={handleCopy} style={{padding:"9px 16px",borderRadius:9,fontSize:12,fontWeight:600,background:copied?"#dcfce7":"#eaeef5",color:copied?"#16a34a":"#1a2540",border:"none",cursor:"pointer"}}>
      {copied ? "✓ Link Copied!" : "🔗 Copy Review Link for Applicant"}
    </button>
  );
}

function Stars({rating}) {
  return <span style={{color:"#f5b301",fontSize:18,letterSpacing:2}}>{"★".repeat(rating)}<span style={{color:"#dde3ed"}}>{"★".repeat(5-rating)}</span></span>;
}

export default function AgentView({ navigate, params }) {
  const { user, logout } = useAuth();
  const { getApp, refresh } = useApp();
  const app = getApp(params?.app_id);

  const Section = ({icon,title,children}) => (
    <div style={{background:"#fff",borderRadius:14,border:"1px solid #e4e9f0",overflow:"hidden",marginBottom:20}}>
      <div style={{padding:"18px 24px",borderBottom:"1px solid #eaeef5",display:"flex",alignItems:"center",gap:12,background:"#fafbfd"}}>
        <div style={{width:38,height:38,borderRadius:10,background:"linear-gradient(135deg,#0d1f3c,#162844)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>{icon}</div>
        <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:600,color:"#0d1f3c"}}>{title}</h2>
      </div>
      <div style={{padding:24}}>{children}</div>
    </div>
  );

  const Grid = ({items}) => (
    <div className="rgrid-3" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
      {items.map(([l,v])=>(
        <div key={l} style={{background:"#f6f8fc",borderRadius:10,padding:"13px 15px"}}>
          <div style={{fontSize:10,letterSpacing:"1.5px",textTransform:"uppercase",color:"#8a9bb5",fontWeight:600}}>{l}</div>
          <div style={{fontSize:13,color:"#1a2540",fontWeight:500,marginTop:4}}>{v||"—"}</div>
        </div>
      ))}
    </div>
  );

  const Nav = () => (
    <nav style={{background:"#0d1f3c",padding:"0 36px",height:62,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:100,borderBottom:"2px solid #4a7fb5"}}>
      <img src="/logo-small.png" alt="AIHOVI" style={{height:52,objectFit:"contain"}} onError={e=>e.target.style.display="none"}/>
      <div style={{display:"flex",alignItems:"center",gap:14}}>
        <div title="Amount paid, as confirmed by admin" style={{background:"linear-gradient(135deg,#1c3660,#2a4570)",border:"1px solid rgba(74,127,181,0.4)",borderRadius:24,padding:"6px 16px",fontSize:13,color:"#fff",display:"flex",alignItems:"center",gap:7,fontWeight:700}}>
          <span style={{fontSize:14}}>💰</span>
          ₹{Number(user?.wallet_balance || 0).toLocaleString("en-IN")}
        </div>
        <button onClick={logout} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.2)",color:"#fff",padding:"6px 15px",borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Sign Out</button>
      </div>
    </nav>
  );

  if (!app || app.agent_email !== user?.email) return (
    <div style={{minHeight:"100vh",background:"#f0f3f9",fontFamily:"'DM Sans',sans-serif"}}>
      <Nav/>
      <div style={{textAlign:"center",padding:60}}>
        <div style={{fontSize:48,marginBottom:16}}>❌</div>
        <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,color:"#0d1f3c",marginBottom:12}}>Application Not Found</h2>
        <button onClick={()=>navigate("agent-dashboard")} style={{padding:"12px 24px",background:"linear-gradient(135deg,#0d1f3c,#162844)",color:"#fff",border:"none",borderRadius:10,fontSize:13,fontWeight:600,cursor:"pointer"}}>← Back to My Applications</button>
      </div>
    </div>
  );

  const needsAttention = app.status === "Rejected" || app.status === "Modify Required";

  return (
    <div style={{minHeight:"100vh",background:"#f0f3f9",fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`@media (max-width:768px){ .av-wrap{padding:16px !important;} }`}</style>
      <Nav/>
      <div className="av-wrap" style={{maxWidth:900,margin:"0 auto",padding:"26px 36px 60px"}}>

        <div style={{background:"linear-gradient(135deg,#0d1f3c,#162844)",borderRadius:16,padding:"26px 30px",marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:16}}>
          <div>
            <h1 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:26,fontWeight:700,color:"#fff",marginBottom:4}}>{app.full_name}</h1>
            <p style={{fontSize:12,color:"#8a9bb5"}}>Submitted {app.submitted_at}</p>
          </div>
          <div style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(74,127,181,0.3)",borderRadius:12,padding:"12px 20px",textAlign:"right"}}>
            <div style={{fontSize:10,letterSpacing:2,color:"#4a7fb5",textTransform:"uppercase",marginBottom:4}}>Reference ID</div>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:700,color:"#fff",letterSpacing:2}}>{app.id}</div>
          </div>
        </div>

        <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap",alignItems:"center"}}>
          <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"9px 18px",borderRadius:10,fontSize:13,fontWeight:600,background:CHIP[app.status]?.bg,color:CHIP[app.status]?.c}}>
            {CHIP[app.status]?.icon} {app.status}
          </span>
          {app.statusUpdatedAt && <span style={{fontSize:12,color:"#8a9bb5"}}>Last updated: {app.statusUpdatedAt}</span>}
          <button onClick={()=>navigate("agent-dashboard")} style={{marginLeft:"auto",padding:"9px 18px",borderRadius:10,fontSize:13,fontWeight:600,background:"#eaeef5",color:"#1a2540",border:"none",cursor:"pointer"}}>← Back to My Applications</button>
        </div>

        <div style={{marginBottom:20,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <ReviewLinkButton appId={app.id}/>
          <button onClick={refresh} style={{padding:"9px 14px",borderRadius:9,fontSize:12,fontWeight:600,background:"#eaeef5",color:"#1a2540",border:"none",cursor:"pointer"}}>🔄 Refresh</button>
          <span style={{fontSize:11,color:"#8a9bb5"}}>If the applicant just submitted a review and it's not showing, click Refresh.</span>
        </div>

        {app.review && (
          <Section icon="⭐" title="Applicant Review">
            <Stars rating={app.review.rating}/>
            <div style={{fontSize:11,color:"#8a9bb5",marginTop:4,marginBottom:10}}>Submitted {app.review.submittedAt}</div>
            {app.review.comment && <p style={{fontSize:13,color:"#1a2540",fontStyle:"italic",background:"#f6f8fc",padding:"12px 14px",borderRadius:10}}>"{app.review.comment}"</p>}
          </Section>
        )}

        {needsAttention && app.statusNote && (
          <div style={{background:app.status==="Rejected"?"#fee2e2":"#fff3d6",border:`1.5px solid ${app.status==="Rejected"?"#dc2626":"#b45309"}`,borderRadius:12,padding:"16px 20px",marginBottom:20}}>
            <strong style={{display:"block",marginBottom:6,color:app.status==="Rejected"?"#991b1b":"#7a5a12",fontSize:14}}>
              {app.status==="Rejected" ? "❌ Why this was rejected:" : "✏️ What needs to be modified:"}
            </strong>
            <p style={{fontSize:13,color:"#3a3a3a",lineHeight:1.6}}>{app.statusNote}</p>
            {app.status==="Modify Required" && (
              <button onClick={()=>navigate("form")} style={{marginTop:12,padding:"9px 18px",borderRadius:9,fontSize:12,fontWeight:600,background:"linear-gradient(135deg,#4a7fb5,#d4736a)",color:"#fff",border:"none",cursor:"pointer"}}>+ Submit Corrected Application</button>
            )}
          </div>
        )}

        {(app.passport_photo || app.passport_copy) && (
          <Section icon="🪪" title="Uploaded Documents">
            <div style={{display:"flex",gap:24,flexWrap:"wrap"}}>
              {app.passport_photo && (
                <div>
                  <img src={app.passport_photo} alt="Applicant" style={{width:100,height:100,objectFit:"cover",borderRadius:12,border:"1px solid #e4e9f0"}}/>
                  <div style={{fontSize:11,color:"#8a9bb5",marginTop:6,textAlign:"center"}}>Applicant Photo</div>
                </div>
              )}
              {app.passport_copy && (
                <div>
                  {app.passport_copy.startsWith("data:image") ? (
                    <img src={app.passport_copy} alt="Passport Copy" style={{width:100,height:100,objectFit:"cover",borderRadius:12,border:"1px solid #e4e9f0"}}/>
                  ) : (
                    <div style={{width:100,height:100,borderRadius:12,border:"1px solid #e4e9f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,background:"#f6f8fc"}}>📄</div>
                  )}
                  <div style={{fontSize:11,color:"#8a9bb5",marginTop:6,textAlign:"center"}}>Passport Copy</div>
                </div>
              )}
            </div>
          </Section>
        )}

        <Section icon="🎯" title="Service Requested">
          <Grid items={[["Service Type",app.service_type],["Submission Time",app.submitted_at]]}/>
        </Section>

        <Section icon="👤" title="Personal Information">
          <Grid items={[["Full Name",app.full_name],["Date of Birth",app.dob],["Gender",app.gender],["Nationality",app.nationality],["Passport Number",app.passport_no],["Passport Expiry",app.passport_expiry],["Email Address",app.email],["Phone",app.phone],["Address",app.address]]}/>
        </Section>

        <Section icon="🗺️" title="Travel Details">
          <Grid items={[["Destination",app.destination],["Travel Purpose",app.travel_purpose],["Travel Date",app.travel_date],["Return Date",app.return_date],["Preferred Airline",app.airline_pref],["Hotel Category",app.hotel_category],["Visa Type",app.visa_type],["Special Requirements",app.special_req||"None"]]}/>
        </Section>
      </div>
    </div>
  );
}
